import { createSyncObservable, useOnNavigation } from '@avensia/excite';
import * as React from 'react';

export type FlyoutType = 'None' | 'Cart' | 'Wishlist' | 'Menu';

export type AuthFlyoutType = 'Login' | 'Registration' | 'ResetPassword' | 'Success' | 'Verification';

let previousActiveElement: Element | null = null;
const [observable] = createSyncObservable<FlyoutType | AuthFlyoutType>('None');

function closeOnNavigation(isOpen: boolean) {
  if (observable.currentSync() !== 'Success' && isOpen) {
    observable.emit('None');
  }
}

export function useFlyoutView() {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [, forceUpdate] = React.useReducer(x => x + 1, 0);
  React.useEffect(() => {
    observable.on(() => forceUpdate());
  }, []);

  return observable.currentSync();
}

export function useFlyout(view: FlyoutType | AuthFlyoutType) {
  // eslint-disable-next-line react-hooks/rules-of-ho oks
  const [, forceUpdate] = React.useReducer(x => x + 1, 0);
  const state = observable.currentSync();
  const isOpen = state === view && state !== 'None';

  React.useEffect(() => {
    const unsubscribe = observable.on(() => forceUpdate());
    return () => unsubscribe();
  }, []);

  const open = React.useCallback(() => {
    previousActiveElement = document.activeElement;
    observable.emit(view);
  }, [view]);

  const close = React.useCallback(() => {
    //Need to set focus in a timeout because of some magic in trapTabbing
    setTimeout(() => {
      if (previousActiveElement instanceof HTMLElement && document.body.contains(previousActiveElement)) {
        previousActiveElement.focus();
      }
    }, 100);
    observable.emit('None');
  }, []);

  const changeView = React.useCallback((view: FlyoutType | AuthFlyoutType) => {
    observable.emit(view);
  }, []);

  useOnNavigation(() => closeOnNavigation(isOpen));

  return { open, close, isOpen, changeView };
}
